import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, LineChart, Line, Legend
} from 'recharts';
import {
  TrendingUp, Users, DollarSign, Vote, Image, Calendar,
  ShoppingCart, Car, Loader2, Award, Trophy, Medal, Target,
  CheckCircle2, Clock, AlertCircle, BarChart3
} from 'lucide-react';
import { cn } from '../../lib/utils';

const COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];

export function AnalyticsDashboard({ tripId }) {
  const { api } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [tripId]);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get(`/trips/${tripId}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError(error.response?.data?.detail || 'Erro ao carregar analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
        <CardContent className="py-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!analytics) return null;

  // Prepare chart data
  const paymentStatusData = analytics.payments_by_status.map(item => ({
    name: item.status,
    value: item.count,
    amount: item.amount
  }));

  const votingData = analytics.voting_participation.map(vote => ({
    name: vote.vote_title.length > 15 ? vote.vote_title.substring(0, 15) + '...' : vote.vote_title,
    participação: vote.participation_rate,
    votos: vote.votes_cast
  }));

  const getMedalIcon = (index) => {
    if (index === 0) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (index === 1) return <Medal className="w-5 h-5 text-slate-400" />;
    if (index === 2) return <Medal className="w-5 h-5 text-amber-600" />;
    return <Award className="w-4 h-4 text-slate-300" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-xl">
          <BarChart3 className="w-6 h-6 text-orange-500" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Dashboard Analytics
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Visão geral da viagem para organizadores
          </p>
        </div>
      </div>

      {/* Overview Cards - Tamanho uniforme no mobile */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="dark:bg-slate-800/50 h-full">
            <CardContent className="p-3 sm:pt-4 h-full">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 h-full">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Participantes</p>
                  <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
                    {analytics.total_members}
                  </p>
                </div>
                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-sky-500" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="dark:bg-slate-800/50 h-full">
            <CardContent className="p-3 sm:pt-4 h-full">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 h-full">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Arrecadação</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600">
                    {analytics.collection_rate.toFixed(0)}%
                  </p>
                </div>
                <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" />
              </div>
              <Progress value={analytics.collection_rate} className="h-1 mt-2" />
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="dark:bg-slate-800/50 h-full">
            <CardContent className="p-3 sm:pt-4 h-full">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 h-full">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Part. Votos</p>
                  <p className="text-xl sm:text-2xl font-bold text-purple-600">
                    {analytics.avg_participation_rate.toFixed(0)}%
                  </p>
                </div>
                <Vote className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="dark:bg-slate-800/50 h-full">
            <CardContent className="p-3 sm:pt-4 h-full">
              <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 h-full">
                <div className="text-center sm:text-left">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Atividades</p>
                  <p className="text-xl sm:text-2xl font-bold text-orange-600">
                    {analytics.itinerary_items_count}
                  </p>
                </div>
                <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Quick Stats Row - Tamanho uniforme no mobile */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center sm:text-left">
          <Image className="w-5 h-5 text-pink-500" />
          <div>
            <p className="text-lg font-semibold text-slate-900 dark:text-white">{analytics.photos_count}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Fotos</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center sm:text-left">
          <ShoppingCart className="w-5 h-5 text-emerald-500" />
          <div>
            <p className="text-lg font-semibold text-slate-900 dark:text-white">{analytics.shopping_lists_count}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Listas</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center sm:text-left">
          <Car className="w-5 h-5 text-cyan-500" />
          <div>
            <p className="text-lg font-semibold text-slate-900 dark:text-white">{analytics.carpools_count}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Caronas</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center sm:text-left">
          <Vote className="w-5 h-5 text-violet-500" />
          <div>
            <p className="text-lg font-semibold text-slate-900 dark:text-white">{analytics.total_votes}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Votações</p>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Payment Status Pie Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="dark:bg-slate-800/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                Status dos Pagamentos
              </CardTitle>
              <CardDescription>
                R$ {analytics.total_collected.toFixed(2)} de R$ {analytics.total_budget.toFixed(2)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={paymentStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {paymentStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [
                        `${value} pagamento(s) - R$ ${props.payload.amount.toFixed(2)}`,
                        name
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Legend */}
              <div className="flex justify-center gap-4 mt-4">
                {paymentStatusData.map((item, index) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: COLORS[index] }}
                    />
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      {item.name}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Voting Participation Bar Chart */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="dark:bg-slate-800/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Vote className="w-5 h-5 text-purple-500" />
                Participação nas Votações
              </CardTitle>
              <CardDescription>
                Média: {analytics.avg_participation_rate.toFixed(0)}% de participação
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                {votingData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={votingData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis type="number" domain={[0, 100]} unit="%" />
                      <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value, name) => [
                          name === 'participação' ? `${value}%` : value,
                          name === 'participação' ? 'Participação' : 'Votos'
                        ]}
                      />
                      <Bar dataKey="participação" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-500 dark:text-slate-400">
                    Nenhuma votação ainda
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Most Active Members */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <Card className="dark:bg-slate-800/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Membros Mais Ativos
            </CardTitle>
            <CardDescription>
              Ranking baseado em pagamentos, votos, fotos e atividades
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.most_active_members.slice(0, 5).map((member, index) => (
                <div
                  key={member.user_id}
                  className={cn(
                    "flex items-center gap-4 p-3 rounded-lg transition-colors",
                    index === 0 ? "bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800" :
                    index === 1 ? "bg-slate-50 dark:bg-slate-700/30" :
                    index === 2 ? "bg-amber-50 dark:bg-amber-900/10" :
                    "bg-slate-50/50 dark:bg-slate-800/30"
                  )}
                >
                  <div className="flex items-center justify-center w-8">
                    {getMedalIcon(index)}
                  </div>
                  
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-lg">
                      {member.avatar || '😊'}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 dark:text-white truncate">
                      {member.username}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-green-500" />
                        {member.payments_made} pgto
                      </span>
                      <span className="flex items-center gap-1">
                        <Vote className="w-3 h-3 text-purple-500" />
                        {member.votes_cast} votos
                      </span>
                      <span className="flex items-center gap-1">
                        <Image className="w-3 h-3 text-pink-500" />
                        {member.photos_uploaded} fotos
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <Badge variant="outline" className="font-mono">
                      {member.activity_score} pts
                    </Badge>
                  </div>
                </div>
              ))}
              
              {analytics.most_active_members.length === 0 && (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  Nenhum dado de atividade ainda
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Payment Timeline */}
      {analytics.payment_timeline.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <Card className="dark:bg-slate-800/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                Histórico de Pagamentos
              </CardTitle>
              <CardDescription>
                Evolução dos pagamentos recebidos ao longo do tempo
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics.payment_timeline}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(date) => {
                        const d = new Date(date);
                        return `${d.getDate()}/${d.getMonth() + 1}`;
                      }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      labelFormatter={(date) => new Date(date).toLocaleDateString('pt-BR')}
                      formatter={(value) => [`R$ ${value.toFixed(2)}`, 'Valor']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="amount" 
                      stroke="#22c55e" 
                      strokeWidth={2}
                      dot={{ fill: '#22c55e' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
